// src/pages/ApprovalPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getDatabase, ref, onValue, get, child, update, set } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import UElogo from "../assets/UElogo_horizontal.png";
import { EMAILJS } from "../config/emailConfig";
import { ArrowBigLeft } from "lucide-react";
import { sendDecisionEmail } from "../utils/sendEmail"
import { fetchAdminLevels, getAdminLevel } from "../utils/userAccess";

// Helpers from your ReservationPage style (small copies)
function formatYMDLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseYMDLocal(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function normalizeTime(time) {
  const [h, m] = time.split(":").map(Number);
  return `${String(h).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
}

// DD/MM/YYYY and DD/MM/YYYY HH:MM (24h)
function formatDateDMY(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}
function formatDateTimeDMY(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

export default function ApprovalPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState(null);
  const [adminLevel, setAdminLevel] = useState(null); // read from your userAccess if you prefer
  const [request, setRequest] = useState(null);
  const [reservations, setReservations] = useState({});
  const [restricted, setRestricted] = useState([]);
  const [unavailable, setUnavailable] = useState([]);
  const [rejectReason, setRejectReason] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (u) => {
      setAuthUser(u || null);
      if (u?.email) {
        try {
          const map = await fetchAdminLevels();
          setAdminLevel(getAdminLevel(u.email, map));
        } catch {
          setAdminLevel(null);
        }
      }
    });
    return () => unsub();
  }, []);

  const allowed = useMemo(() => {
    const matchEmail =
      (authUser?.email || "").toLowerCase() ===
        (EMAILJS?.approvals?.approverEmail || "").toLowerCase();    return matchEmail || adminLevel === 1 || adminLevel === 0;
  }, [authUser, adminLevel]);

  useEffect(() => {
    const db = getDatabase();
    // Load the request
    onValue(ref(db, `approvalRequests/${id}`), (snap) => {
      setRequest(snap.val());
    });
    // Current reservations
    onValue(ref(db, "reservations/buildingA"), (snap) => {
      setReservations(snap.val() || {});
    });
    // Restricted CSV
    onValue(ref(db, "uploads/class_schedulesA"), (snap) => {
      const content = snap.val()?.content;
      if (!content) return;
      // Lazy parsing just for approve-time checks
      // We only need StartTime, EndTime, Week, Classroom
      import("papaparse").then(({ default: Papa }) =>
        Papa.parse(content, {
          header: true,
          skipEmptyLines: true,
          complete: ({ data }) => {
            const processed = data.map((row) => {
              const weekArr = (row.Week || "").split(",").map((s) => s.trim()).filter(Boolean);
              return {
                Classroom: row.Classroom?.trim(),
                StartTime: row.StartTime?.trim(),
                EndTime: row.EndTime?.trim(),
                Week: weekArr,
              };
            });
            setRestricted(processed);
          },
        })
      );
    });
    // Unavailable CSV
    onValue(ref(db, "uploads/unavailable_schedules"), (snap) => {
      const content = snap.val()?.content;
      if (!content) return;
      import("papaparse").then(({ default: Papa }) =>
        Papa.parse(content, {
          header: true,
          skipEmptyLines: true,
          complete: ({ data }) => {
            const processed = data
            .filter((r) => r.Classroom && r.FromDate && r.ToDate)
            .flatMap((row) => {
              const classroom = row.Classroom.trim();
              const start = row.StartTime ? normalizeTime(row.StartTime) : "00:00";
              const end   = row.EndTime ? normalizeTime(row.EndTime) : "24:00";
              const fromDate = row.FromDate.trim();
              const toDate   = row.ToDate.trim();
              const recurring = row.Recurring?.trim();

              const out = [];
              for (let d = parseYMDLocal(fromDate); d <= parseYMDLocal(toDate); d.setDate(d.getDate() + 1)) {
                const dayIndex = d.getDay();
                const dateStr = formatYMDLocal(d);
                // reuse the same recurrence rules used on ReservationPage (short form)
                const match =
                  !recurring ||
                    (recurring === "Sun" && dayIndex === 0) ||
                    (recurring === "Mon" && dayIndex === 1) ||
                    (recurring === "Tue" && dayIndex === 2) ||
                    (recurring === "Wed" && dayIndex === 3) ||
                    (recurring === "Thu" && dayIndex === 4) ||
                    (recurring === "Fri" && dayIndex === 5) ||
                    (recurring === "Sat" && dayIndex === 6) ||
                    (recurring === "Mon-Thu" && dayIndex >= 1 && dayIndex <= 4) ||
                    (recurring === "Mon-Fri" && dayIndex >= 1 && dayIndex <= 5) ||
                    (recurring === "Sat-Sun" && (dayIndex === 6 || dayIndex === 0));
                if (match) {
                  out.push({ Classroom: classroom, StartTime: start, EndTime: end, Week: [dateStr] });
                }
              }
              return out;
            });
            setUnavailable(processed);
          },
        })
      );
    });
  }, [id]);

  // 🔎 Check if a slot is still bookable now
  function isSlotFreeAndAllowed(room, dateKey, time) {
    // already reserved?
    if (reservations?.[room]?.[dateKey]?.[time]) return false;

    // restricted?
    const r1 = restricted.find(
      (res) =>
        res.Classroom === room &&
          res.Week?.includes?.(dateKey) &&
          normalizeTime(time) >= normalizeTime(res.StartTime) &&
          normalizeTime(time) <  normalizeTime(res.EndTime)
    );
    if (r1) return false;

    // unavailable?
    const r2 = unavailable.find(
      (res) =>
        (res.Classroom === room || res.Classroom?.toUpperCase() === "ALL") &&
          res.Week?.includes?.(dateKey) &&
          normalizeTime(time) >= normalizeTime(res.StartTime) &&
          normalizeTime(time) <  normalizeTime(res.EndTime)
    );
    if (r2) return false;

    return true;
  }


  async function approve() {
  setError("");
  if (!allowed) { setError("You are not allowed to approve this request."); return; }
  if (!request || request.status !== "pending") return;

  const db = getDatabase();
  const updates = {};

  // validate slots are still free
  for (const { dateKey, time } of request.period.slots) {
    if (!isSlotFreeAndAllowed(request.room, dateKey, time)) {
      setError(`Slot ${dateKey} ${time} is no longer available.`);
      return;
    }
  }

  const reviewer = {
    name: getAuth().currentUser?.displayName || "",
    email: getAuth().currentUser?.email || "",
  };

  // mark request as approved + persist reviewer + optional comment
  updates[`approvalRequests/${id}/status`] = "approved";
  updates[`approvalRequests/${id}/decision`] = {
    by: reviewer,                       // <-- store both name + email
    at: new Date().toISOString(),
    reason: (rejectReason || "").trim() // optional comment even on approvals
  };

  // write reservations
  const reservePayload = {
    status: "Reserved",
    email: request.requestedBy.email,
    user: request.requestedBy.name,
  };
  for (const { dateKey, time } of request.period.slots) {
    updates[`reservations/buildingA/${request.room}/${dateKey}/${time}`] = reservePayload;
  }

  await update(ref(db), updates);

  // notify requester
  await sendDecisionEmail({
    to_email: request.requestedBy.email,
    to_name: request.requestedBy.name || "Requester",
    room: request.room,
    begin_date: request.period.startISO,
    end_date: request.period.endISO,
    motive: request.motive || "",
    status: "APPROVED",
    reason: (rejectReason || "").trim(),
  });
}

  async function reject() {
  setError("");
  if (!allowed) { setError("You are not allowed to reject this request."); return; }
  if (!request || request.status !== "pending") return;
  if (!rejectReason.trim()) { setError("Please provide a justification to reject."); return; }

  const db = getDatabase();
  const reviewer = {
    name: getAuth().currentUser?.displayName || "",
    email: getAuth().currentUser?.email || "",
  };

  await update(ref(db, `approvalRequests/${id}`), {
    status: "rejected",
    decision: {
      by: reviewer,                     
      at: new Date().toISOString(),
      reason: rejectReason.trim(),      
    },
  });

  await sendDecisionEmail({
    to_email: request.requestedBy.email,
    to_name: request.requestedBy.name || "Requester",
    room: request.room,
    begin_date: request.period.startISO,
    end_date: request.period.endISO,
    motive: request.motive || "",
    status: "REJECTED",
    reason: rejectReason.trim(),
  });
}

  const goBack = () => navigate(-1);

  if (!request) {
    return (
      <div className="flex flex-col p-6 pt-28 min-h-screen bg-gray-800 text-gray-100">
        <div className="w-full flex justify-between items-center px-6 py-4 absolute top-0 left-0">
          <img src={UElogo} alt="UE Logo" className="h-12 w-auto" />
        </div>
        <p>Loading request…</p>
      </div>
    );
  }

  const statusClass =
    request.status === "approved"
      ? "text-green-400"
      : request.status === "rejected"
        ? "text-red-400"
        : "text-yellow-400"; // optional: pending = yellow

  function renderReviewer(by) {
    if (!by) return "";
    if (typeof by === "string") return by; // backward-compat if older requests saved only an email string
    const name = by.name || "";
    const email = by.email || by.mail || "";
    if (name && email) return `${name} (${email})`;
    return name || email || "";
  }



  return (
    <div className="flex flex-col p-6 pt-28 min-h-screen bg-gray-800 text-gray-100">
      <div className="w-full flex justify-between items-center px-6 py-4 absolute top-0 left-0">
        <img src={UElogo} alt="UE Logo" className="h-12 w-auto" />
        <button onClick={goBack} className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded">
          <ArrowBigLeft className="w-5 h-5 text-white" />
        </button>
      </div>

      {!allowed ? (
        <div className="text-red-400">You are not allowed to view this page.</div>
      ) : (
          <>
            <h1 className="text-xl font-bold mb-4">Approval Request</h1>
            <div className="space-y-2 mb-4">
              <div><strong>Status:</strong> {request.status}</div>
              <div><strong>Room:</strong> {request.room}</div>
              <div><strong>Requester:</strong> {request.requestedBy.name} ({request.requestedBy.email})</div>
              <div><strong>From:</strong> {formatDateTimeDMY(request.period.startISO)}</div>
              <div><strong>To:</strong> {formatDateTimeDMY(request.period.endISO)}</div>
              <div><strong>Motive:</strong> {request.motive}</div>
              {request.decision?.by && (
                <div><strong>Reviewed by:</strong> {renderReviewer(request.decision.by)}</div>
              )}
              {request.decision?.at && (
                <div><strong>Decision date:</strong> {new Date(request.decision.at).toLocaleString("en-GB")}</div>
              )}
              {request.decision?.reason && (
                <div><strong>Justification / Comment:</strong> {request.decision.reason}</div>
              )}
            </div>

            {error && <p className="text-red-400 mb-3">{error}</p>}

            {request.status === "pending" ? (
              <div className="space-y-3">
                <button onClick={approve} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded">
                  Approve & Reserve
                </button>

                <div className="mt-2">
                  <label className="block mb-1 text-sm">Reject justification / Comment</label>
                  <textarea
                    className="w-full p-2 rounded bg-gray-700 text-white"
                    rows={3}
                    value={rejectReason}
                    onChange={(e)=>setRejectReason(e.target.value)}
                    placeholder="Reason for rejection…"
                  />
                  <button onClick={reject} className="mt-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded">
                    Reject
                  </button>
                </div>
              </div>
            ) : (
                <div className="text-gray-300">
                  This request is{" "}
                  <strong className={statusClass}>{request.status}</strong>.
                </div>

              )}
          </>
        )}
    </div>
  );
}

